import { useEffect, useRef, useState } from 'react';
import { request, toast } from '../../../../../../utils';
import { CircularProgress, IconButton, Stack, Typography, TextField as MuiTextField, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Table } from '../../../../../../components';

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

const TABLE_HEADER = [
  { header: 'IPD No.', accessor: 'ipd_no' },
  { header: 'UHID No.', accessor: 'uhid' },
  { header: 'Patient Name', accessor: (row) => `${row?.patient?.first_name || ''} ${row?.patient?.last_name || ''}`.trim() },
  { header: 'Admission Date', accessor: (row) => formatDate(row?.admissionDate) },
  { header: 'Address', accessor: 'patient.address.address' },
  { header: 'Sponsor', accessor: 'patient.sponsor.name' },
  { header: 'Mobile No.', accessor: 'patient.address.mobile' }
];

export const IPDSearchHandler = ({ onClose, onAdd = () => {}, ongetBalance = () => {} }) => {
  const [patientData, setPatientData] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const isFirstRender = useRef(true);

  // Single unified fetch — used for both initial load and search
  const fetchPatients = async (searchTerm = '') => {
    setLoading(true);
    try {
      const query = {};
      if (searchTerm.trim()) query.patientName = searchTerm.trim();

      const response = await request('/indoor-registeration-detail-list', {
        method: 'GET',
        query
      });

      if (response?.success && Array.isArray(response.data)) {
        // Filter to only currently admitted patients
        const admitted = response.data.filter(
          (item) => item.admission_status === 'admit'
        );
        setPatientData(admitted);
      } else {
        toast('Failed to load IPD list', { variant: 'error' });
        setPatientData([]);
      }
    } catch (error) {
      console.error('IPD search error:', error);
      toast('Error loading IPD data', { variant: 'error' });
      setPatientData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount (once)
  useEffect(() => {
    fetchPatients();
  }, []);

  // Debounced search — skip on first render (initial load handled above), fire 400ms after user types
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchPatients(input);
    }, 400);
    return () => clearTimeout(timer);
  }, [input]);

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor="grey.300"
        pb={1}
      >
        <Typography variant="h4" fontWeight="bold">
          Indoor Registeration Search
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon color="error" fontSize="large" />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
        <MuiTextField
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': { padding: '0', fontSize: '1.4rem' },
            '& .MuiOutlinedInput-input': { padding: '8px 1.4rem' },
            '& .MuiInputLabel-root': { fontSize: '1.2rem' }
          }}
          label="Search by UHID / IPD No. / Name / Mobile"
          variant="outlined"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          InputProps={{ style: { fontSize: '12px', fontWeight: 600 } }}
        />
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2, fontSize: '1.4rem', color: 'text.secondary' }}>
            Loading patients...
          </Typography>
        </Box>
      ) : (
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_HEADER}
          data={patientData}
          onRowClick={(row) => {
            onAdd(row.ipd_no);
            ongetBalance(row?.ipd_no);
            onClose();
          }}
        />
      )}
    </>
  );
};
